import { useEffect, useState } from 'react'

import type { OnChangeHandlerType } from '@/components/uikit'
import {
  useLazyGetGeocodeQuery,
  useLazyGetLocationAutocompleteQuery,
} from '@/slices/autocomplete'

import {
  getGeocodeDataByPlaceId,
  getPlacePredictions,
  getSelectedOption,
} from './helpers'

import type {
  onSearchType,
  OptionType,
  usePlaceAutocompleteType,
  SelectedOptionType,
} from './types'

export const usePlaceAutocomplete: usePlaceAutocompleteType = ({
  value,
  onChange,
  initialInputValue = '',
  officeSuggestions = [],
}) => {
  const [getLocationAutocomplete] = useLazyGetLocationAutocompleteQuery()
  const [getGeocode] = useLazyGetGeocodeQuery()
  const [inputValue, setInputValue] = useState(initialInputValue)
  const [options, setOptions] = useState<OptionType[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    setInputValue(initialInputValue)
  }, [initialInputValue])

  useEffect(() => {
    if (!inputValue && value && getLocationAutocomplete) {
      updateOptionsWithPredictions(value)
    }
  }, [inputValue, value, getLocationAutocomplete])

  const updateOptionsWithPredictions = async (input: string) => {
    const googleOptions = await getPlacePredictions(getLocationAutocomplete, {
      input,
    })

    setOptions(googleOptions || [])
  }

  const handleChange = (
    selectedValue?: string | null,
    selectedOption?: SelectedOptionType
  ) => {
    setInputValue(selectedValue)
    onChange(selectedValue, selectedOption)
  }

  const onSearch: onSearchType = (searchText, { action, prevInputValue }) => {
    if (action === 'input-change') {
      setInputValue(searchText)
      if (!searchText || !getLocationAutocomplete) {
        setOptions([])
        handleChange()
        return
      }

      if (getLocationAutocomplete) {
        updateOptionsWithPredictions(searchText)
      }
      return searchText
    }

    return prevInputValue
  }

  const filteredOffices =
    inputValue && inputValue.length > 0
      ? officeSuggestions.filter(
          (office) =>
            office.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            office.description
              .toLowerCase()
              .includes(inputValue.toLowerCase()) ||
            office.address?.toLowerCase().includes(inputValue.toLowerCase())
        )
      : []

  const combinedOptions = [...filteredOffices, ...(options || [])]

  const onChangeHandler: OnChangeHandlerType<string> = async (selected) => {
    try {
      const selectedOption = getSelectedOption(selected, combinedOptions)
      if (selectedOption) {
        setIsLoading(true)

        if (selectedOption.isOffice) {
          handleChange(selectedOption.description, {
            ...selectedOption,
            lat: selectedOption.lat,
            lon: selectedOption.lon,
            address: selectedOption.address,
          })
        } else {
          const geocodeData = await getGeocodeDataByPlaceId(
            getGeocode,
            selectedOption.value
          )

          handleChange(selectedOption.description, {
            ...selectedOption,
            ...geocodeData,
          })
        }
      } else {
        setOptions([])
        handleChange(null, null)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    options: combinedOptions,
    onSearch,
    onChangeHandler,
    inputValue,
    isLoading,
  }
}
